import React, { useState, useEffect, useRef } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useClerk, useAuth } from '@clerk/clerk-react';
import { useDispatch, useSelector } from 'react-redux';

import { ensureSocket, disconnectSocket } from './socketClient';
import { useAuthData } from './hooks/useAuthData';
import useAmplitude from './hooks/useAmplitude';
import { setClerkInstance } from './utils/apiService';
import { setDigestServerHead } from './reducers/digestReducer';
import {
  clearScheduledMailFailures,
  retainScheduledMailFailure,
} from './reducers/emailJobReducer';

import Sidebar from './components/Sidebar';
import Project from './components/Project/Project';
// import Dashboard from './components/Dashboard/Dashboard';
import TaskCategory from './components/Task/TaskCategory';
import Task from './components/Task/Task';
import Digest from './components/Digest/Digest';
import Login from './components/Auth/Login';
import AdminRoute from './components/Auth/AdminRoute';
import MicrosoftMailboxBootstrap from './components/Auth/MicrosoftMailboxBootstrap';
import ProjectList from './components/Project/ProjectList';
import TaskList from './components/Task/TaskList';
import ResponsePartnerDashboard from './components/ResponsePartner/ResponsePartnerDashboard';
import { useLoading } from './components/Auth/LoadingProvider';
import LoadingSpinner from './components/Shared/LoadingSpinner';
import DigestReadyBadge from './components/Shared/DigestReadyBadge';
import './App.css';

const App = () => {
  const dispatch = useDispatch();
  const { getToken, userId } = useAuth();
  const location = useLocation();
  const { isSignedIn, isInitialLoadComplete } = useAuthData();
  const { initAmplitude } = useAmplitude();
  const clerk = useClerk();
  const { isDataLoading } = useLoading();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [isDigestReady, setIsDigestReady] = useState(false);
  
  const digests = useSelector((state) => state.digestList?.digests || []);
  const digestsRef = useRef(digests);

  // Keep ref in sync with latest digests
  useEffect(() => {
    digestsRef.current = digests;
  }, [digests]);

  useEffect(() => {
    initAmplitude();
  }, [initAmplitude]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    
    window.addEventListener('resize', handleResize)
    handleResize() // Initialize on mount
    
    return () => window.removeEventListener('resize', handleResize)
  }, [])  

  useEffect(() => {
    if (clerk) {
      setClerkInstance(clerk);
    }
  }, [clerk]);

  useEffect(() => {
    let mounted = true;
    let activeSocket;
    let onDigestReady;
    let onDigestFailure;
    let onFocus;

    (async () => {
      if (!isSignedIn) {
        disconnectSocket();
        return;
      }
      try {
        activeSocket = await ensureSocket(getToken);
        if (!mounted) return;

        onDigestReady = async (payload) => {
          if (payload?.head?.source === 'schedule') {
            dispatch(clearScheduledMailFailures(payload?.mailboxId));
          }
          dispatch(setDigestServerHead(payload));

          setTimeout(() => {
            const digest = digestsRef.current.find(d => d.id === payload.digestId);
            if(digest?.serverHead?.source === "schedule") {
              setIsDigestReady(true);
            }
          }, 1500);
        };

        activeSocket.on('digest:cron:done', onDigestReady);

        onDigestFailure = (payload) => {
          dispatch(retainScheduledMailFailure(payload));
        };
        activeSocket.on('digest:cron:failed', onDigestFailure);

        onFocus = () => {
          if (location.pathname && !location.pathname.startsWith('/digest')) {
          }
        };
        window.addEventListener('focus', onFocus);
      } catch (error) {
        if (mounted) {
          console.warn('Background socket connection unavailable:', error?.message || error);
        }
      }
    })();

    return () => {
      mounted = false;
      if (activeSocket && onDigestReady) {
        activeSocket.off('digest:cron:done', onDigestReady);
      }
      if (activeSocket && onDigestFailure) {
        activeSocket.off('digest:cron:failed', onDigestFailure);
      }
      if (onFocus) {
        window.removeEventListener('focus', onFocus);
      }
    };
  }, [isSignedIn, getToken, dispatch, location.pathname, userId]);  

  const getIsMobileOnly = () => {
    return windowWidth < 768
  }

  if (!isSignedIn) {  
    return (
      <div className="flex h-screen w-screen">
        <Login />
      </div>
    )
  }  
  
  if (isDataLoading || !isInitialLoadComplete) {
    return <LoadingSpinner />;
  }

  return (
    <div className={`app-container flex ${getIsMobileOnly() ? 'flex-col h-screen' : 'flex-row h-full'}`}>
      {isDigestReady && <DigestReadyBadge 
        onClear={() => setIsDigestReady(false)} 
      />}
      {<Sidebar />}
      <div className="flex h-screen w-screen">
          <MicrosoftMailboxBootstrap>
            <Routes>
              <Route path="/" element={<Navigate to="/digest" />} />
              <Route path="/project/new" element={<Project />} />
              <Route path="/project/:id" element={<Project />} />
              <Route path="/projects" element={<ProjectList />} />
              <Route path="/task/category/:categoryId" element={<TaskCategory />} />
              <Route path="/tasks" element={<TaskList />} />
              {/* <Route path="/dashboard" element={<Dashboard />} /> */}
              <Route path="/digest" element={<Digest />} />
              <Route path="/digest/:digestDate" element={<Digest />} />
              <Route path="/task/:id" element={<Task />} />
              <Route
                path="/admin/response-partners"
                element={
                  <AdminRoute>
                    <ResponsePartnerDashboard />
                  </AdminRoute>
                }
              />
              <Route path="/login" element={<Login />} />
            </Routes>
          </MicrosoftMailboxBootstrap>
      </div>
    </div>
  );
};

export default App;
